/* Núcleo de cálculo de precificação — puro (sem React, sem Firestore, sem
   efeito colateral). Único lugar do app que sabe compor custo e resolver
   preço; Calculadora, e futuramente Produtos/Pedidos, chamam essas funções
   em vez de terem sua própria cópia da fórmula.

   ── Conceitos (nunca misturados) ──────────────────────────────────────
   markup = preco / custo                    (multiplicador sobre o custo)
   margem = lucro / preco × 100               (% sobre o preço de venda)
   lucro  = preco − taxas − custo             (R$, sempre líquido de taxas)

   ── Taxas (plataforma, pagamento, comissão, imposto) ──────────────────
   Todas incidem sobre a MESMA base (o preço de venda), então são somadas
   num único divisor combinado — igual ao padrão já usado e correto em
   src/constants/maquininhas.js (calcularRepasse) e
   src/constants/lojasOnline.js (calcularPrecoLoja):
     preco = valorLiquido / (1 − Σtaxa%)
   Dividir taxa por taxa (sequencial) sub-cobraria: duas taxas de 10% dão
   19% no sequencial contra 20% correto no somado — por isso a soma flat.

   ── Os 3 modos de resolver preço (custo=R$50, taxas=15%) ──────────────
   modo:'lucro', alvo=R$50  → preco=(50+50)/0,85=R$117,65 → lucro real R$50,00 ✓
   modo:'margem', alvo=30%  → preco=50/(1−0,15−0,30)=R$90,91 → margem real 30% ✓
   modo:'preco', alvo=R$100 → deriva taxas=R$15, lucro=R$35, margem=35%, markup=2,0

   Por quê os divisores são diferentes: variáveis já em R$ (lucro-alvo,
   custo) entram SOMADAS no numerador; variáveis que são % do próprio preço
   final ainda desconhecido (taxas, margem-alvo) entram SUBTRAÍDAS no
   divisor. Usar o divisor errado num modo dá um preço que não fecha a
   conta de volta (ver engine.test.js). */

const num = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const pos = (v, def = 0) => Math.max(0, num(v, def));

/** custo_total de um item de filamento a partir do peso usado e do custo por kg cadastrado. */
export function custoItemFilamento(pesoG, custoPorKg) {
  return pos(pesoG) * (pos(custoPorKg) / 1000);
}

/**
 * @typedef {Object} ConfigEnergia
 * @property {'simples'|'avancado'} modo
 * @property {number} horas
 * @property {number} [valorHora]    // modo 'simples' — R$/h (equivalente ao antigo consumo_hr)
 * @property {number} [potenciaW]    // modo 'avancado'
 * @property {number} [valorKwh]     // modo 'avancado', R$ por kWh
 */
export function custoEnergia(cfg) {
  if (!cfg) return 0;
  const horas = pos(cfg.horas);
  if (cfg.modo === "avancado") {
    const kw = pos(cfg.potenciaW) / 1000;
    return kw * horas * pos(cfg.valorKwh);
  }
  return pos(cfg.valorHora) * horas;
}

/**
 * @typedef {Object} ConfigDesgaste
 * @property {'simples'|'avancado'} modo
 * @property {number} horas
 * @property {number} [valorHora]        // modo 'simples' — R$/h (equivalente ao antigo desgaste_hr)
 * @property {number} [aquisicao]        // modo 'avancado' — preço de compra da impressora
 * @property {number} [vidaUtilHoras]    // modo 'avancado' — vida útil total estimada
 * @property {number} [manutencaoTotal]  // modo 'avancado' — manutenção total esperada na vida útil
 */
export function custoDesgaste(cfg) {
  if (!cfg) return 0;
  const horas = pos(cfg.horas);
  if (cfg.modo === "avancado") {
    const vidaUtil = pos(cfg.vidaUtilHoras);
    if (vidaUtil <= 0) return 0;
    const custoHora = (pos(cfg.aquisicao) + pos(cfg.manutencaoTotal)) / vidaUtil;
    return custoHora * horas;
  }
  return pos(cfg.valorHora) * horas;
}

/**
 * @typedef {Object} LinhaCusto
 * @property {string} categoria  'filamento'|'materiaisExtras'|'maoDeObra'|'energia'|'desgaste'|'adicional'
 * @property {string} label
 * @property {number} valor
 */

/**
 * @typedef {Object} ItemCustoAdicional
 * @property {string} nome
 * @property {number} valor
 * @property {'fixo'|'por_unidade'|'percentual'} tipo
 * @property {number} [quantidade]  // usado só quando tipo === 'por_unidade'
 */

/**
 * Soma todos os componentes de custo num único custoTotal, mantendo o
 * detalhamento linha a linha pra exibição transparente ("Ver como o preço
 * foi calculado"). Função pura — recebe os números já resolvidos.
 *
 * `custosAdicionais` do tipo 'percentual' incide sobre o custoBase (tudo
 * ANTES dos próprios adicionais) — nunca sobre o preço de venda (isso é
 * papel de `taxas`) e nunca sobre outro item da mesma lista (evita
 * depender da ordem dos itens).
 *
 * @param {Object} input
 * @param {{custo_total:number}[]} [input.filamentos]
 * @param {{custo_unitario:number, qtd:number}[]} [input.materiaisExtras]
 * @param {ItemCustoAdicional[]} [input.custosAdicionais]
 * @param {{horas:number, valorHora:number}} [input.maoDeObra]
 * @param {ConfigEnergia} [input.energia]
 * @param {ConfigDesgaste} [input.desgaste]
 * @returns {{ custoTotal: number, custoBase: number, detalhamento: LinhaCusto[] }}
 */
export function somarCustos({
  filamentos = [], materiaisExtras = [], custosAdicionais = [],
  maoDeObra = null, energia = null, desgaste = null,
} = {}) {
  const detalhamento = [];
  let custoTotal = 0;

  const custoFilamentos = filamentos.reduce((s, f) => s + pos(f.custo_total), 0);
  if (filamentos.length) detalhamento.push({ categoria: "filamento", label: "Filamento", valor: custoFilamentos });
  custoTotal += custoFilamentos;

  const custoMateriais = materiaisExtras.reduce((s, m) => s + pos(m.custo_unitario) * pos(m.qtd, 1), 0);
  if (materiaisExtras.length) detalhamento.push({ categoria: "materiaisExtras", label: "Materiais extras", valor: custoMateriais });
  custoTotal += custoMateriais;

  const custoMO = maoDeObra ? pos(maoDeObra.horas) * pos(maoDeObra.valorHora) : 0;
  if (custoMO > 0) detalhamento.push({ categoria: "maoDeObra", label: "Mão de obra", valor: custoMO });
  custoTotal += custoMO;

  const custoEn = custoEnergia(energia);
  if (custoEn > 0) detalhamento.push({ categoria: "energia", label: "Energia", valor: custoEn });
  custoTotal += custoEn;

  const custoDes = custoDesgaste(desgaste);
  if (custoDes > 0) detalhamento.push({ categoria: "desgaste", label: "Desgaste da impressora", valor: custoDes });
  custoTotal += custoDes;

  const custoBase = custoTotal; // referência do 'percentual' dos adicionais — não inclui os próprios adicionais
  let custoAdicionais = 0;
  for (const item of custosAdicionais) {
    let valor;
    if (item.tipo === "percentual") valor = custoBase * (pos(item.valor) / 100);
    else if (item.tipo === "por_unidade") valor = pos(item.valor) * pos(item.quantidade, 1);
    else valor = pos(item.valor); // 'fixo'
    detalhamento.push({ categoria: "adicional", label: item.nome || "Custo adicional", valor });
    custoAdicionais += valor;
  }
  custoTotal += custoAdicionais;

  return { custoTotal, custoBase, detalhamento };
}

/**
 * @typedef {Object} Taxa
 * @property {string} [id]
 * @property {string} nome
 * @property {number} pct   // "12" significa 12%, mesma convenção de maquininhas.js/lojasOnline.js
 */

/** Soma simples das taxas — todas incidem sobre a mesma base (o preço final). */
export function somarTaxas(taxas) {
  const totalPct = (taxas || []).reduce((s, t) => s + pos(t.pct), 0);
  return { totalPct, totalFrac: totalPct / 100 };
}

/**
 * @typedef {Object} ResultadoPreco
 * @property {number} precoFinal
 * @property {number} custoTotal
 * @property {number} taxaTotalPct
 * @property {number} taxaTotalR$
 * @property {number} valorLiquido   // precoFinal − taxaTotalR$
 * @property {number} lucroReal      // R$, já líquido de taxas
 * @property {number} margemReal     // % sobre o preço (margem de verdade, não markup)
 * @property {number} markup         // multiplicador sobre o custo
 * @property {boolean} [inviavel]    // true quando as taxas/margem-alvo somam >= 100% (preço infinito)
 */

/**
 * Dado um preço já conhecido (calculado ou digitado direto pelo usuário),
 * deriva todo o resto — usada tanto pelo modo 'preco' quanto pra
 * recalcular em tempo real quando o usuário edita o preço na mão.
 *
 * @param {{ precoFinal:number, custoTotal:number, taxas?:Taxa[] }} p
 * @returns {ResultadoPreco}
 */
export function analisarPreco({ precoFinal, custoTotal, taxas = [] }) {
  const preco = pos(precoFinal);
  const custo = pos(custoTotal);
  const { totalPct, totalFrac } = somarTaxas(taxas);
  const taxaTotalR$ = preco * totalFrac;
  const valorLiquido = preco - taxaTotalR$;
  const lucroReal = valorLiquido - custo;
  const margemReal = preco > 0 ? (lucroReal / preco) * 100 : 0;
  const markup = custo > 0 ? preco / custo : 0;
  return { precoFinal: preco, custoTotal: custo, taxaTotalPct: totalPct, taxaTotalR$, valorLiquido, lucroReal, margemReal, markup };
}

/**
 * Resolve o preço final a partir de um dos 3 objetivos de venda.
 *
 * @param {Object} p
 * @param {number} p.custoTotal
 * @param {Taxa[]} [p.taxas]
 * @param {'margem'|'lucro'|'preco'} p.modo
 * @param {number} p.valorAlvo
 *   modo 'margem' → valorAlvo é % (ex.: 30 = 30% de margem sobre o preço)
 *   modo 'lucro'  → valorAlvo é R$ (lucro absoluto desejado)
 *   modo 'preco'  → valorAlvo é R$ (o preço final já decidido)
 * @returns {ResultadoPreco}
 */
export function resolverPreco({ custoTotal, taxas = [], modo, valorAlvo }) {
  const custo = pos(custoTotal);
  const alvo = num(valorAlvo);
  const { totalFrac } = somarTaxas(taxas);

  if (modo === "preco") {
    return analisarPreco({ precoFinal: alvo, custoTotal: custo, taxas });
  }

  if (modo === "lucro") {
    const divisor = 1 - totalFrac;
    if (divisor <= 0) return { ...analisarPreco({ precoFinal: 0, custoTotal: custo, taxas }), inviavel: true };
    return analisarPreco({ precoFinal: (custo + alvo) / divisor, custoTotal: custo, taxas });
  }

  if (modo === "margem") {
    const divisor = 1 - totalFrac - alvo / 100;
    if (divisor <= 0) return { ...analisarPreco({ precoFinal: 0, custoTotal: custo, taxas }), inviavel: true };
    return analisarPreco({ precoFinal: custo / divisor, custoTotal: custo, taxas });
  }

  throw new Error(`resolverPreco: modo inválido "${modo}" (use 'margem'|'lucro'|'preco')`);
}
